<script setup>
import { ref, watch, computed, defineProps, defineEmits, defineExpose } from 'vue'
import Dialog from 'primevue/dialog'
import { useI18n } from '@/i18n'

const { t } = useI18n()

const props = defineProps({
  /* Sichtbarkeit via v-model */
  modelValue: { type: Boolean, default: false },

  /* Titel in der Standard-Header-Leiste (überschreibbar via slot="header") */
  title: { type: String, default: '' },

  /* Footer: Standard-pButtons anzeigen? (überschreibbar via slot="footer") */
  showFooter: { type: Boolean, default: true },
  submitLabel: { type: String, default: '' },
  cancelLabel: { type: String, default: '' },

  /* Ux / State */
  loading: { type: Boolean, default: false },
  disableOutsideClose: { type: Boolean, default: false },
  closable: { type: Boolean, default: true }, // X-pButton
  blockScroll: { type: Boolean, default: true },
  modal: { type: Boolean, default: true },

  /**
   * optionaler Guard vorm Schließen:
   * async/boolean Funktion; wenn false => nicht schließen
   * Beispiel: () => !isDirty || confirm('Verwerfen?')
   */
  beforeClose: { type: Function, default: null },

  /**
   * Größe:
   * - preset: 'sm' | 'md' | 'lg' | 'xl' (breite in rem)
   * - oder direkt per width z.B. '720px' / '80vw'
   */
  size: { type: String, default: 'md' },
  width: { type: String, default: '' },

  /* Optional: maximierbar */
  maximizable: { type: Boolean, default: false },

  /* Body-Padding entfernen (z.B. für randlose Tabellen / Picker) */
  flush: { type: Boolean, default: false },

  /* ARIA */
  ariaLabel: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'submit', 'cancel', 'show', 'hide'])

const visible = ref(props.modelValue)
watch(() => props.modelValue, v => visible.value = v)
watch(visible, v => emit('update:modelValue', v))

const presetWidth = computed(() => {
  if (props.width) return props.width
  switch (props.size) {
    case 'sm': return '420px'
    case 'md': return '640px'
    case 'lg': return '820px'
    case 'xl': return '1024px'
    default:   return '640px'
  }
})

async function requestClose(reason = 'close') {
  if (props.beforeClose) {
    const ok = await props.beforeClose(reason)
    if (!ok) return
  }
  visible.value = false
}

function onHide() {
  emit('hide')
}
function onShow() {
  emit('show')
}

/* Public API für Elternkomponenten (optional) */
defineExpose({
  open: () => { visible.value = true },
  close: () => requestClose('api')
})

function onSubmit() {
  emit('submit')
}
function onCancel() {
  emit('cancel')
  requestClose('cancel')
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :modal="modal"
    :closable="closable"
    :dismissable-mask="!disableOutsideClose"
    :blockScroll="blockScroll"
    :maximizable="maximizable"
    class="app-dialog"
    :style="{ width: presetWidth }"
    :aria-label="ariaLabel || title"
    @hide="onHide"
    @show="onShow"
  >
    <!-- Header -->
    <template #header>
      <slot name="header">
        <div class="app-dialog__header">
          <h3 class="title">{{ title }}</h3>
          <!-- Optionaler Action-Slot rechts im Header -->
          <div class="actions">
            <slot name="header-actions" />
          </div>
        </div>
      </slot>
    </template>

    <!-- Body -->
    <div :class="['app-dialog__body', { 'app-dialog__body--flush': flush }]">
      <!-- Optional: als Form verwenden; Parent fängt 'submit' ab -->
      <form @submit.prevent="onSubmit">
        <slot />
        <!-- versteckter Submit-pButton, damit Enter funktioniert -->
        <pButton type="submit" style="display:none" />
      </form>
    </div>

    <!-- Footer -->
    <template #footer>
      <slot name="footer" v-if="!showFooter" />
      <div v-else class="app-dialog__footer">
        <pButton
          :label="cancelLabel || t('common.cancel')"
          severity="secondary"
          :disabled="loading"
          @click="onCancel"
        />
        <pButton
          :label="submitLabel || t('common.save')"
          :loading="loading"
          @click="onSubmit"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.app-dialog :deep(.p-dialog) {
  /* runde Ecken, dezenter Shadow */
  border-radius: 14px;
}
.app-dialog :deep(.p-dialog-header) {
  padding: 0.75rem 1rem;
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--p-surface-0);
  border-bottom: 1px solid var(--p-content-border-color);
}
.app-dialog__header {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}
.app-dialog__header .title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  flex: 1;
}
.app-dialog__header .actions:empty { display: none; }

.app-dialog__body {
  padding: 0.75rem 1rem;
  max-height: 70vh;
  overflow: auto;
}
.app-dialog__body--flush {
  padding: 0;
}

.app-dialog :deep(.p-dialog-footer) {
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: var(--p-surface-0);
  border-top: 1px solid var(--p-content-border-color);
}
.app-dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  padding: 0.5rem 0;
}
</style>
